import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add, CloudDownloadOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SurveyTable from "../../components/SurveyTable";
import { ensureNamespaceLoaded } from "../../i18n/lazyNamespaces";
import { usePageContext } from "../../context/PageContext";
import { can, toSet, useEffectivePermissions } from "../../hooks/useEffectivePermissions";
import { useSurveyDetails, useSurveyTemplates, useSurveyResponses, useSurveyAnalytics, useSurveys, useWorkspaceSurveySummary } from "../../hooks/useSurveys";
import SurveyBuilderDialog from "./SurveyBuilderDialog";
import SurveyPeriodFilterBar from "./SurveyPeriodFilterBar";
import { buildSurveyPeriodFilters, getDefaultSurveyPeriod } from "./surveyPeriod";

const SummaryCard = ({ label, value, helper }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 1, fontWeight: 700 }}>
        {value}
      </Typography>
      {helper ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {helper}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

const toSafeSlug = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

const EMPTY_LEGACY_SYNC_RESULT = {
  definitionsImported: 0,
  projectsUpdated: 0,
  questionsImported: 0,
  sessionsImported: 0,
  responsesImported: 0,
  alreadyMapped: 0,
  skippedEmptySurveys: 0,
  responsesSkippedNoQuestions: 0,
  hasMore: false,
  pendingRoots: 0,
};

const mergeLegacySyncResults = (accumulated = {}, batch = {}) => ({
  definitionsImported:
    Number(accumulated.definitionsImported || 0) + Number(batch.definitionsImported || 0),
  projectsUpdated: Number(accumulated.projectsUpdated || 0) + Number(batch.projectsUpdated || 0),
  questionsImported:
    Number(accumulated.questionsImported || 0) + Number(batch.questionsImported || 0),
  sessionsImported: Number(accumulated.sessionsImported || 0) + Number(batch.sessionsImported || 0),
  responsesImported:
    Number(accumulated.responsesImported || 0) + Number(batch.responsesImported || 0),
  alreadyMapped: Math.max(
    Number(accumulated.alreadyMapped || 0),
    Number(batch.alreadyMapped || 0)
  ),
  skippedEmptySurveys: Math.max(
    Number(accumulated.skippedEmptySurveys || 0),
    Number(batch.skippedEmptySurveys || 0)
  ),
  responsesSkippedNoQuestions:
    Number(accumulated.responsesSkippedNoQuestions || 0) +
    Number(batch.responsesSkippedNoQuestions || 0),
  hasMore: Boolean(batch.hasMore),
  pendingRoots: Number(batch.pendingRoots ?? accumulated.pendingRoots ?? 0),
});

const hasLegacySyncNewData = (result = {}) =>
  Number(result.definitionsImported) > 0 ||
  Number(result.questionsImported) > 0 ||
  Number(result.responsesImported) > 0 ||
  Number(result.sessionsImported) > 0 ||
  Number(result.projectsUpdated) > 0;

const summarizeLegacySyncBatch = (result = {}, t) => {
  const parts = [];
  if (Number(result.sessionsImported) > 0) {
    parts.push(t("legacyImport.batchSessions", { count: result.sessionsImported }));
  }
  if (Number(result.responsesImported) > 0) {
    parts.push(t("legacyImport.batchResponses", { count: result.responsesImported }));
  }
  if (Number(result.questionsImported) > 0) {
    parts.push(t("legacyImport.batchQuestions", { count: result.questionsImported }));
  }
  if (Number(result.definitionsImported) > 0) {
    parts.push(t("legacyImport.batchSurveys", { count: result.definitionsImported }));
  }
  if (Number(result.projectsUpdated) > 0) {
    parts.push(t("legacyImport.batchProjects", { count: result.projectsUpdated }));
  }
  return parts.length ? parts.join(", ") : t("legacyImport.batchNone");
};

const formatLegacySyncProgressMessage = (result, batchNumber, cumulative, t) =>
  t("legacyImport.progress", {
    batch: batchNumber,
    batchSummary: summarizeLegacySyncBatch(result, t),
    totalSummary: summarizeLegacySyncBatch(cumulative, t),
  });

const formatLegacySyncFinalMessage = (result = {}, t) => {
  const pendingRoots = Number(result.pendingRoots || 0);

  if (result.hasMore) {
    if (hasLegacySyncNewData(result)) {
      return t("legacyImport.partial", {
        sessions: Number(result.sessionsImported || 0),
        responses: Number(result.responsesImported || 0),
        pending: pendingRoots,
      });
    }
    if (pendingRoots > 0) {
      return t("legacyImport.partialPending", { pending: pendingRoots });
    }
    return t("legacyImport.alreadySynced", {
      count: Number(result.alreadyMapped || result.surveyCount || 0),
    });
  }

  if (hasLegacySyncNewData(result)) {
    return t("legacyImport.success", {
      summary: summarizeLegacySyncBatch(result, t),
    });
  }

  if (Number(result.responsesSkippedNoQuestions) > 0) {
    return t("legacyImport.skippedAnswers", {
      count: Number(result.responsesSkippedNoQuestions),
    });
  }

  if (Number(result.alreadyMapped) > 0 || pendingRoots === 0) {
    return t("legacyImport.alreadySynced", {
      count: Number(result.alreadyMapped || result.surveyCount || 0),
    });
  }

  return t("legacyImport.empty");
};

const getLegacySyncAlertSeverity = (result = {}) => {
  if (result.hasMore) return "warning";
  return hasLegacySyncNewData(result) ? "success" : "info";
};

const Surveys = () => {
  const { t } = useTranslation("surveys");
  React.useEffect(() => {
    ensureNamespaceLoaded("surveys");
  }, []);
  const { currentPage, setCurrentPage } = usePageContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { data: permissionData } = useEffectivePermissions();
  const permissionSet = toSet(permissionData?.scopes);

  const canCreate = can(permissionSet, "surveys", "create");
  const canUpdate = can(permissionSet, "surveys", "update");
  const canDelete = can(permissionSet, "surveys", "delete");

  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [infoSeverity, setInfoSeverity] = useState("success");
  const [legacyImportBatch, setLegacyImportBatch] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [builderState, setBuilderState] = useState({ open: false, surveyId: null, survey: null });
  const defaultPeriod = React.useMemo(() => getDefaultSurveyPeriod(), []);
  const [hubStartDate, setHubStartDate] = useState(defaultPeriod.startDate);
  const [hubEndDate, setHubEndDate] = useState(defaultPeriod.endDate);
  const hubPeriodFilters = React.useMemo(
    () => buildSurveyPeriodFilters(hubStartDate, hubEndDate),
    [hubStartDate, hubEndDate]
  );

  const {
    data,
    error,
    refetch,
    isLoading,
    saveMutation,
    updateStatusMutation,
    deleteMutation,
    syncLegacyMutation,
  } = useSurveys(currentPage, pageSize, search, statusFilter);

  const { data: workspaceSummary } = useWorkspaceSurveySummary(hubPeriodFilters);

  const { data: templates = [] } = useSurveyTemplates();
  const { data: editingSurvey } = useSurveyDetails(builderState.surveyId, builderState.open);

  const surveys = data?.surveys || [];
  const totalSurveys = workspaceSummary?.totalSurveys ?? data?.pagination?.total ?? surveys.length;
  const totalPublished = workspaceSummary?.publishedSurveys ?? surveys.filter((survey) => survey.status === "published").length;
  const totalCompleted = workspaceSummary?.completedCount ?? 0;
  const completionRate = workspaceSummary?.completionRate ?? null;
  const publishedWithoutResponses = useMemo(
    () =>
      surveys.filter(
        (survey) => survey.status === "published" && Number(survey.summary?.completedCount || 0) === 0
      ),
    [surveys]
  );
  const publishedAlertCount =
    workspaceSummary?.publishedWithoutResponses ?? publishedWithoutResponses.length;

  const activeBuilderSurvey = builderState.surveyId ? editingSurvey : builderState.survey;

  React.useEffect(() => {
    if (error) {
      setErrorMessage(error.message || t("errors.loadFailed"));
    }
  }, [error, t]);

  const handleSearch = (term) => {
    setCurrentPage(1);
    setSearch(term);
  };

  const handleDeleteSurvey = (surveyId) => {
    setConfirmDelete(surveyId);
    setErrorMessage(null);
  };

  const confirmDeleteSurvey = () => {
    if (!confirmDelete) return;
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setConfirmDelete(null);
        refetch();
      },
      onError: (err) => {
        setErrorMessage(err?.message || "Erro ao excluir a pesquisa.");
      },
    });
  };

  const handleSaveSurvey = (payload) => {
    saveMutation.mutate(payload, {
      onSuccess: () => {
        setBuilderState({ open: false, surveyId: null, survey: null });
        refetch();
      },
      onError: (err) => {
        if (Number(err?.status) === 409) {
          setErrorMessage(err?.message || t("errors.saveBlocked"));
          return;
        }
        setErrorMessage(err?.message || t("errors.saveFailed"));
      },
    });
  };

  const handleStatusChange = (surveyId, status) => {
    setErrorMessage(null);
    updateStatusMutation.mutate(
      { id: surveyId, status },
      {
        onSuccess: () => {
          refetch();
        },
        onError: (err) => {
          setErrorMessage(err?.message || t("errors.statusUpdateFailed"));
        },
      }
    );
  };

  const handleDeleteSelected = async (ids) => {
    const results = await Promise.allSettled(ids.map((id) => deleteMutation.mutateAsync(id)));
    const failed = results.filter((result) => result.status === "rejected");
    if (failed.length) {
      setErrorMessage(
        failed.length === ids.length
          ? "Nenhuma pesquisa selecionada foi excluída."
          : `${failed.length} pesquisa(s) não puderam ser excluídas.`
      );
    }
    refetch();
  };

  const handleOpenPublic = (survey) => {
    const company = localStorage.getItem("company");
    if (!company) {
      setErrorMessage(t("errors.workspaceRequired"));
      return;
    }
    const explicitSlug = String(survey?.slug || "").trim();
    const slug =
      explicitSlug && explicitSlug !== "undefined" && explicitSlug !== "null"
        ? explicitSlug
        : survey?.id
        ? `survey-${survey.id}`
        : toSafeSlug(survey?.title || survey?.titulo);
    if (!slug) {
      setErrorMessage("Esta pesquisa ainda não possui slug público. Salve/atualize a pesquisa para gerar o link.");
      return;
    }
    window.open(
      `${window.location.origin}/public/surveys/${slug}?company=${encodeURIComponent(company)}`,
      "_blank"
    );
  };

  const runLegacySync = React.useCallback(
    (attempt = 1, accumulated = null) => {
      const cumulative = accumulated || { ...EMPTY_LEGACY_SYNC_RESULT };
      setLegacyImportBatch(attempt);
      syncLegacyMutation.mutate(
        { includeResponses: true, limit: 10000, timeBudgetMs: 25000 },
        {
          onSuccess: (response) => {
            const result = response?.data || response || {};
            const merged = mergeLegacySyncResults(cumulative, result);

            if (result.hasMore && attempt < 100) {
              setInfoSeverity("info");
              setInfoMessage(formatLegacySyncProgressMessage(result, attempt, merged, t));
              runLegacySync(attempt + 1, merged);
              return;
            }

            setLegacyImportBatch(null);
            setInfoSeverity(getLegacySyncAlertSeverity(merged));
            setInfoMessage(formatLegacySyncFinalMessage(merged, t));
            refetch();
          },
          onError: (err) => {
            setLegacyImportBatch(null);
            const isTimeout = /timeout/i.test(String(err?.message || ""));
            setInfoMessage(null);
            setErrorMessage(
              isTimeout ? t("errors.legacyImportTimeout") : err?.message || t("errors.legacyImportFailed")
            );
            refetch();
          },
        }
      );
    },
    [refetch, syncLegacyMutation, t]
  );

  return (
    <Box sx={{ pt: 1 }}>
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "stretch" : "flex-start"}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box sx={{ maxWidth: 640 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {t("title")}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {t("subtitle")}
          </Typography>
        </Box>
        <Stack direction={isMobile ? "column" : "row"} spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            variant="text"
            color="inherit"
            disabled={syncLegacyMutation.isLoading}
            startIcon={
              syncLegacyMutation.isLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <CloudDownloadOutlined />
              )
            }
            onClick={() => {
              setErrorMessage(null);
              setInfoMessage(null);
              setLegacyImportBatch(null);
              runLegacySync(1);
            }}
            sx={{ color: "text.secondary" }}
          >
            {syncLegacyMutation.isLoading
              ? legacyImportBatch
                ? t("actions.importingLegacyBatch", { batch: legacyImportBatch })
                : t("actions.importingLegacy")
              : t("actions.importLegacy")}
          </Button>
          {canCreate ? (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setBuilderState({ open: true, surveyId: null, survey: {} })}
            >
              {t("actions.newSurvey")}
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {errorMessage ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      ) : null}

      {infoMessage ? (
        <Alert severity={infoSeverity} sx={{ mb: 2 }} onClose={() => setInfoMessage(null)}>
          {infoMessage}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent sx={{ py: 2 }}>
              <SurveyPeriodFilterBar
                compact
                startDate={hubStartDate}
                endDate={hubEndDate}
                onStartDateChange={setHubStartDate}
                onEndDateChange={setHubEndDate}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <SummaryCard
            label={t("cards.totalSurveys")}
            value={totalSurveys}
            helper={t("cards.publishedCount", { count: totalPublished })}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard
            label={t("cards.completedResponses")}
            value={totalCompleted}
            helper={t("cards.completedResponsesPeriodHelper")}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard
            label={t("cards.completionRate")}
            value={completionRate == null ? "—" : `${completionRate}%`}
            helper={t("cards.completionRatePeriodHelper")}
          />
        </Grid>
      </Grid>

      {!surveys.length && !isLoading ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            canCreate ? (
              <Button color="inherit" size="small" onClick={() => setBuilderState({ open: true, surveyId: null, survey: {} })}>
                {t("actions.newSurvey")}
              </Button>
            ) : null
          }
        >
          {t("empty.noSurveys")}
        </Alert>
      ) : null}

      {publishedAlertCount > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: publishedWithoutResponses.length > 0 ? 1 : 0 }}>
            {t("alerts.publishedNoResponses", { count: publishedAlertCount })}
          </Typography>
          {publishedWithoutResponses.length > 0 && publishedWithoutResponses.length <= 3 ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {publishedWithoutResponses.map((survey) => (
                <Chip
                  key={survey.id}
                  size="small"
                  label={survey.title || survey.titulo || `#${survey.id}`}
                  clickable={canUpdate}
                  onClick={
                    canUpdate
                      ? () => setBuilderState({ open: true, surveyId: survey.id, survey })
                      : undefined
                  }
                />
              ))}
            </Stack>
          ) : null}
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Chip
          label={t("filters.all")}
          clickable
          color={!statusFilter ? "primary" : "default"}
          variant={!statusFilter ? "filled" : "outlined"}
          onClick={() => {
            setCurrentPage(1);
            setStatusFilter("");
          }}
        />
        <Chip
          label={t("filters.published")}
          clickable
          color={statusFilter === "published" ? "success" : "default"}
          variant={statusFilter === "published" ? "filled" : "outlined"}
          onClick={() => {
            setCurrentPage(1);
            setStatusFilter("published");
          }}
        />
        <Chip
          label={t("filters.draft")}
          clickable
          color={statusFilter === "draft" ? "warning" : "default"}
          variant={statusFilter === "draft" ? "filled" : "outlined"}
          onClick={() => {
            setCurrentPage(1);
            setStatusFilter("draft");
          }}
        />
        <Chip
          label={t("filters.archived")}
          clickable
          color={statusFilter === "archived" ? "default" : "default"}
          variant={statusFilter === "archived" ? "filled" : "outlined"}
          onClick={() => {
            setCurrentPage(1);
            setStatusFilter("archived");
          }}
        />
      </Stack>

      <SurveyTable
        surveys={surveys}
        rowCount={data?.pagination?.total || 0}
        pageSize={pageSize}
        page={(data?.pagination?.page || currentPage) - 1}
        onPageChange={(page) => setCurrentPage(page + 1)}
        onPageSizeChange={setPageSize}
        onSearch={handleSearch}
        isLoading={
          isLoading ||
          saveMutation.isLoading ||
          updateStatusMutation.isLoading ||
          syncLegacyMutation.isLoading
        }
        onEditSurvey={(survey) => setBuilderState({ open: true, surveyId: survey.id, survey })}
        onDeleteSurvey={handleDeleteSurvey}
        onDeleteSelected={handleDeleteSelected}
        onViewAnalytics={(survey) => navigate(`/surveys/${survey.id}/analytics`)}
        onOpenPublic={handleOpenPublic}
        onStatusChange={canUpdate ? handleStatusChange : undefined}
        statusUpdatingIds={
          updateStatusMutation.isLoading && updateStatusMutation.variables?.id
            ? { [updateStatusMutation.variables.id]: true }
            : {}
        }
        canEdit={canUpdate}
        canDelete={canDelete}
      />

      <SurveyBuilderDialog
        open={builderState.open}
        templates={templates}
        initialSurvey={activeBuilderSurvey}
        loading={saveMutation.isLoading}
        onClose={() => setBuilderState({ open: false, surveyId: null, survey: null })}
        onSubmit={handleSaveSurvey}
      />

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Excluir pesquisa</DialogTitle>
        <DialogContent>
          <Typography>
            Esta ação faz exclusão lógica da pesquisa. Respostas históricas continuam preservadas
            para auditoria.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
          <Button color="error" onClick={confirmDeleteSurvey}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Surveys;
